import { useState, useEffect, useCallback } from 'react'
import { MdAdd, MdPerson, MdFlight, MdLoyalty, MdLocationOn } from 'react-icons/md'
import { useDispatch, useSelector } from 'react-redux'
import { PassengerCard } from '../../components/cards'
import { Button, LoadingSpinner, EmptyState, SearchBar, FilterBar } from '../../components/ui'
import { usePassenger } from '../../hooks/usePassenger'
import { selectPassengerListUpdated, setPassengerListUpdated } from '../../state/websocket.slice'

function PassengerList({ onViewPassenger, onEditPassenger, onDeletePassenger }) {
  const dispatch = useDispatch()
  const [passengers, setPassengers] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filter, setFilter] = useState('all') // all, active, inactive, premium
  const [isLoading, setIsLoading] = useState(true)
  const passengerListUpdated = useSelector(selectPassengerListUpdated)

  const { getPassengersAsync, deletePassengerAsync } = usePassenger()

  const loadPassengers = useCallback(async () => {
    try {
      setIsLoading(true)
      const passengersData = await getPassengersAsync()
      setPassengers(passengersData || [])
    } catch (error) {
      console.error('Error loading passengers:', error)
    } finally {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadPassengers()
  }, [loadPassengers])

  useEffect(() => {
    if (passengerListUpdated) {
      setPassengers((prevPassengers) => {
        const existingIndex = prevPassengers.findIndex(
          (passenger) => passenger._id.toString() === passengerListUpdated._id.toString(),
        )
        if (existingIndex !== -1) {
          const updatedPassengers = [...prevPassengers]
          updatedPassengers[existingIndex] = passengerListUpdated
          return updatedPassengers
        } else {
          return [...prevPassengers, passengerListUpdated]
        }
      })
      dispatch(setPassengerListUpdated(undefined))
    }
  }, [passengerListUpdated]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeletePassenger = async (passenger) => {
    try {
      await deletePassengerAsync(passenger._id)
      await loadPassengers() // Refresh the list
      if (onDeletePassenger) {
        onDeletePassenger(passenger)
      }
    } catch (error) {
      console.error('Error deleting passenger:', error)
    }
  }

  const handleViewPassenger = (passenger) => {
    if (onViewPassenger) {
      onViewPassenger(passenger)
    }
  }

  const handleEditPassenger = (passenger) => {
    if (onEditPassenger) {
      onEditPassenger(passenger)
    }
  }

  const getFilteredPassengers = () => {
    let filteredPassengers = passengers

    if (filter === 'active') {
      filteredPassengers = passengers.filter((passenger) => passenger.status === 'active')
    } else if (filter === 'inactive') {
      filteredPassengers = passengers.filter((passenger) => passenger.status === 'inactive')
    } else if (filter === 'premium') {
      filteredPassengers = passengers.filter(
        (passenger) =>
          passenger.loyaltyProgram?.points > 10000 ||
          passenger.loyaltyProgram?.tier === 'premium' ||
          passenger.loyaltyProgram?.tier === 'gold' ||
          passenger.loyaltyProgram?.tier === 'platinum',
      )
    }

    if (searchTerm) {
      filteredPassengers = filteredPassengers.filter(
        (passenger) =>
          `${passenger.firstName} ${passenger.lastName}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          passenger.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          passenger.passport?.number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          passenger.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          passenger.nationality?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    return filteredPassengers
  }

  const filteredPassengers = getFilteredPassengers()

  const getActiveCount = () =>
    passengers.filter((passenger) => passenger.status === 'active').length
  const getInactiveCount = () =>
    passengers.filter((passenger) => passenger.status === 'inactive').length
  const getPremiumCount = () =>
    passengers.filter(
      (passenger) =>
        passenger.loyaltyProgram?.points > 10000 ||
        passenger.loyaltyProgram?.tier === 'premium' ||
        passenger.loyaltyProgram?.tier === 'gold' ||
        passenger.loyaltyProgram?.tier === 'platinum',
    ).length
  const getTotalCount = () => passengers.length

  const getTotalLoyaltyPoints = () => {
    return passengers.reduce((total, passenger) => {
      return total + (passenger.loyaltyProgram?.points || 0)
    }, 0)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with search and filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search passengers..."
        />

        <div className="flex items-center space-x-3">
          <FilterBar
            filterValue={filter}
            onFilterChange={(e) => setFilter(e.target.value)}
            filterOptions={[
              { value: 'all', label: 'All Passengers' },
              { value: 'active', label: `Active (${getActiveCount()})` },
              { value: 'inactive', label: `Inactive (${getInactiveCount()})` },
              { value: 'premium', label: `Premium (${getPremiumCount()})` },
            ]}
          />

          <Button
            variant="primary"
            size="md"
            onClick={() => {
              /* TODO: Implement create passenger */
            }}
            className="flex items-center space-x-2"
          >
            <MdAdd className="text-lg" />
            <span>Add Passenger</span>
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                Total Passengers
              </p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {getTotalCount()}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
              <MdPerson className="text-primary-600 dark:text-primary-400 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                Active Passengers
              </p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {getActiveCount()}
              </p>
            </div>
            <div className="w-12 h-12 bg-success-100 dark:bg-success-900 rounded-lg flex items-center justify-center">
              <MdFlight className="text-success-600 dark:text-success-400 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                Premium Members
              </p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {getPremiumCount()}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
              <MdLoyalty className="text-yellow-600 dark:text-yellow-400 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                Total Loyalty Points
              </p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {getTotalLoyaltyPoints().toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <MdLocationOn className="text-blue-600 dark:text-blue-400 text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Passenger Grid */}
      {filteredPassengers.length === 0 ? (
        <EmptyState
          icon={<MdPerson className="text-4xl text-neutral-400" />}
          title="No passengers found"
          description={
            searchTerm || filter !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'Get started by adding your first passenger'
          }
          action={
            <Button
              variant="primary"
              onClick={() => {
                /* TODO: Implement create passenger */
              }}
              className="flex items-center space-x-2"
            >
              <MdAdd className="text-lg" />
              <span>Add Passenger</span>
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPassengers.map((passenger) => (
            <PassengerCard
              key={passenger._id}
              passenger={passenger}
              onView={() => handleViewPassenger(passenger)}
              onEdit={() => handleEditPassenger(passenger)}
              onDelete={() => handleDeletePassenger(passenger)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default PassengerList
